/**
 * FDI Collections System - State Manager
 *
 * Centralises REST interactions, optimistic updates, selection handling,
 * and exposes a small event emitter for UI modules.
 */

import CollectionAPI from './CollectionAPI.js';
import { normaliseCollectionError } from './error-utils.js';

class CollectionManager {
    constructor(api = null, options = {}) {
        this.api = api || new CollectionAPI();
        this.options = {
            optimisticUpdates: true,
            ...options,
        };

        this.state = {
            collections: [],
            currentFdiId: null,
            loading: false,
            error: null,
            filters: {
                search: '',
                tag: '',
                visibility: 'all',
                orderby: 'created_at',
                order: 'desc',
            },
            pagination: {
                page: 1,
                perPage: 20,
                totalItems: 0,
                totalPages: 1,
            },
            selection: new Set(),
            assigned: new Set(),
        };

        this.emitter = new EventTarget();
        this.optimisticSnapshots = new Map();
        this.lastFailedAction = null;
    }

    /**
     * Subscribe to manager events.
     */
    on(event, callback) {
        const handler = (evt) => callback(evt.detail);
        this.emitter.addEventListener(event, handler);
        return () => this.emitter.removeEventListener(event, handler);
    }

    /**
     * Emit event with detail payload.
     */
    emit(event, detail = {}) {
        this.emitter.dispatchEvent(new CustomEvent(event, { detail }));
    }

    /**
     * Get a serialisable snapshot of state.
     */
    getState() {
        return {
            ...this.state,
            selection: new Set(this.state.selection),
            assigned: new Set(this.state.assigned),
        };
    }

    /**
     * Initialise with contextual FDI ID.
     */
    async initialize(fdiId) {
        this.state.currentFdiId = fdiId || null;
        this.state.selection.clear();
        this.state.assigned.clear();
        this.emit('state:change', this.getState());
        await this.loadCollections({ page: 1 });
    }

    /**
     * Update filters and reload.
     */
    async setFilters(partial = {}) {
        this.state.filters = {
            ...this.state.filters,
            ...partial,
        };
        this.emit('filters:change', this.getState());
        return this.loadCollections({ page: 1 });
    }

    /**
     * Update pagination page.
     */
    async setPage(page) {
        if (page === this.state.pagination.page) {
            return;
        }
        this.state.pagination.page = page;
        this.emit('state:change', this.getState());
        return this.loadCollections({ page });
    }

    /**
     * Update per-page value.
     */
    async setPerPage(perPage) {
        this.state.pagination.perPage = perPage;
        this.emit('state:change', this.getState());
        return this.loadCollections({ page: 1, perPage });
    }

    /**
     * Toggle selection for a collection ID.
     */
    toggleSelection(collectionId) {
        const id = Number(collectionId);
        if (!id) return;

        const nextSelection = new Set(this.state.selection);
        if (nextSelection.has(id)) {
            nextSelection.delete(id);
        } else {
            nextSelection.add(id);
        }
        this.state.selection = nextSelection;
        const snapshot = this.getState();
        this.emit('selection:change', snapshot);
    }

    setSelection(ids = []) {
        this.state.selection = new Set(ids.map((id) => Number(id)).filter(Boolean));
        const snapshot = this.getState();
        this.emit('selection:change', snapshot);
    }

    clearSelection() {
        this.state.selection.clear();
        const snapshot = this.getState();
        this.emit('selection:change', snapshot);
    }

    /**
     * Persist selected collections against current FDI item.
     * Optimized to use single sync API call instead of separate add/remove requests.
     */
    async persistSelection() {
        if (!this.state.currentFdiId) {
            return { added: [], removed: [] };
        }

        const selected = Array.from(this.state.selection);
        const assigned = Array.from(this.state.assigned);

        const toAdd = selected.filter((id) => !this.state.assigned.has(id));
        const toRemove = assigned.filter((id) => !this.state.selection.has(id));

        if (!toAdd.length && !toRemove.length) {
            return { added: [], removed: [] };
        }

        this.#setLoading(true);

        try {
            /* Use optimized sync API for combined add/remove operation */
            const payload = await this.api.syncItems(toAdd, toRemove, this.state.currentFdiId);
            
            /* Process sync results */
            const addedResults = payload?.added?.results || [];
            const removedResults = payload?.removed?.results || [];
            const summary = payload?.summary || { total_added: 0, total_removed: 0 };

            /* Apply results to collections state */
            this.#applySyncResults(addedResults, removedResults);

            /* Update assigned state to match current selection */
            this.state.assigned = new Set(this.state.selection);
            
            /* Emit events */
            this.emit('selection:saved', { 
                added: toAdd, 
                removed: toRemove,
                summary: summary,
            });
            
            this.#setLoading(false);
            const snapshot = this.getState();
            this.emit('selection:change', snapshot);
            this.emit('state:change', snapshot);
            
            return { 
                added: toAdd, 
                removed: toRemove,
                summary: summary,
            };
        } catch (error) {
            this.#setLoading(false);
            this.lastFailedAction = () => this.persistSelection();
            const detail = this.#emitError('items:error', error, {
                context: 'persist',
                fallback: 'Không thể cập nhật danh sách yêu thích.',
                retryable: Boolean(error?.retryable),
                retry: this.lastFailedAction,
            });
            this.emit('state:change', this.getState());
            throw new Error(detail.error);
        }
    }

    /**
     * Load collections from API.
     */
    async loadCollections({ page, perPage } = {}) {
        this.#setLoading(true);
        this.state.error = null;
        this.emit('state:change', this.getState());

        const targetPage = page ?? this.state.pagination.page;
        const targetPerPage = perPage ?? this.state.pagination.perPage;

        try {
            const response = await this.api.listCollections({
                page: targetPage,
                per_page: targetPerPage,
                search: this.state.filters.search,
                tag: this.state.filters.tag,
                visibility: this.state.filters.visibility,
                orderby: this.state.filters.orderby,
                order: this.state.filters.order,
                fdi_id: this.state.currentFdiId,
            });

            const collections = Array.isArray(response?.collections) ? response.collections : [];
            const pagination = response?.pagination || {};

            this.state.collections = collections.map((collection) => ({
                ...collection,
                is_public: collection.is_public === true || collection.is_public === 1 || collection.is_public === '1',
                has_fdi: collection.has_fdi === true || collection.has_fdi === 1 || collection.has_fdi === '1',
            }));

            const assignedIds = this.state.collections
                .filter((collection) => collection.has_fdi)
                .map((collection) => Number(collection.id));
            const currentCollectionIds = new Set(this.state.collections.map(c => Number(c.id)));

            // Update assigned from server truth
            this.state.assigned = new Set(assignedIds);
            
            // Preserve user's selection for items still in current page
            // Add newly assigned items, keep existing selections that are still visible
            const oldSelection = new Set(this.state.selection);
            const newSelection = new Set();
            
            // Add all assigned (saved) items
            assignedIds.forEach(id => newSelection.add(Number(id)));
            
            // Keep user's selections that are still in current collections
            this.state.selection.forEach(id => {
                const numId = Number(id);
                if (currentCollectionIds.has(numId)) {
                    newSelection.add(numId);
                }
            });
            
            this.state.selection = newSelection;

            this.state.pagination = {
                page: Number(pagination.currentPage) || targetPage,
                perPage: Number(pagination.perPage) || targetPerPage,
                totalItems: Number(pagination.total) || this.state.collections.length,
                totalPages: Number(pagination.pages) || 1,
            };

            this.#setLoading(false);
            const snapshot = this.getState();
            this.emit('selection:change', snapshot);
            this.emit('collections:loaded', snapshot);
            this.emit('state:change', snapshot);
        } catch (error) {
            this.#setLoading(false);
            const retryAction = () => this.loadCollections({ page: targetPage, perPage: targetPerPage });
            this.lastFailedAction = retryAction;
            const detail = this.#emitError('collections:error', error, {
                context: 'load',
                fallback: 'Không thể tải danh sách. Vui lòng thử lại.',
                retryable: Boolean(error?.retryable),
                retry: retryAction,
            });
            this.state.error = detail.error;
            this.emit('state:change', this.getState());
            throw new Error(detail.error);
        }
    }

    /**
     * Create collection and insert into state.
     */
    /**
     * Create collection via API and manage state.
     */
    async createCollection(data, { autoSelect = false } = {}) {
        this.#setLoading(true);
        this.emit('state:change', this.getState());

        try {
            const collection = await this.api.createCollection(data);
            if (!collection?.id) {
                throw new Error('Invalid collection response');
            }

            this.state.collections = [
                { ...collection, has_fdi: false },
                ...this.state.collections,
            ];

            let selectionChanged = false;
            if (autoSelect) {
                this.state.selection.add(Number(collection.id));
                selectionChanged = true;
            }

            // Keep pagination totals in sync when new item inserted.
            this.state.pagination.totalItems += 1;
            this.state.pagination.totalPages = Math.max(
                1,
                Math.ceil(this.state.pagination.totalItems / this.state.pagination.perPage),
            );

            this.#setLoading(false);
            const snapshot = this.getState();
            this.emit('collection:created', { collection });
            if (selectionChanged) {
                this.emit('selection:change', snapshot);
            }
            this.emit('state:change', snapshot);
            return collection;
        } catch (error) {
            this.#setLoading(false);
            const retryAction = () => this.createCollection(data, { autoSelect });
            this.lastFailedAction = retryAction;
            const detail = this.#emitError('collection:error', error, {
                context: 'create',
                fallback: 'Không thể tạo danh sách mới.',
                retryable: Boolean(error?.retryable),
                retry: retryAction,
            });
            this.state.error = detail.error;
            this.emit('state:change', this.getState());
            throw new Error(detail.error);
        }
    }

    /**
     * Destroy manager and cancel active requests.
     */
    destroy() {
        if (typeof this.api.cancelAll === 'function') {
            this.api.cancelAll();
        }
        this.emitter = new EventTarget();
        this.optimisticSnapshots.clear();
    }

    /**
     * --- Internal helpers -------------------------------------------------
     */

    async #addToCollections(collectionIds, skipSelectionSync = false) {
        const ids = collectionIds.map((id) => Number(id)).filter(Boolean);
        if (!ids.length) return;

        const previousStates = this.#captureOptimisticStates(ids);

        if (this.options.optimisticUpdates) {
            this.#applyOptimistic(ids, true);
        }

        try {
            const payload = await this.api.addItems(ids, this.state.currentFdiId);
            this.#applyOperationResults(payload, ids, true);
            if (!skipSelectionSync) {
                ids.forEach((id) => {
                    this.state.selection.add(id);
                    this.state.assigned.add(id);
                });
                this.emit('selection:change', this.getState());
            }
            this.emit('items:added', { collectionIds: ids });
        } catch (error) {
            this.#rollbackOptimistic(previousStates);
            const retryAction = () => this.#addToCollections(collectionIds, skipSelectionSync);
            this.lastFailedAction = retryAction;
            const detail = this.#emitError('items:error', error, {
                context: 'add',
                fallback: 'Không thể thêm dự án vào danh sách đã chọn.',
                retryable: Boolean(error?.retryable),
                retry: retryAction,
            });
            throw new Error(detail.error);
        }
    }

    async #removeFromCollections(collectionIds, skipSelectionSync = false) {
        const ids = collectionIds.map((id) => Number(id)).filter(Boolean);
        if (!ids.length) return;

        const previousStates = this.#captureOptimisticStates(ids);

        if (this.options.optimisticUpdates) {
            this.#applyOptimistic(ids, false);
        }

        try {
            const payload = await this.api.removeItems(ids, this.state.currentFdiId);
            this.#applyOperationResults(payload, ids, false);
            if (!skipSelectionSync) {
                ids.forEach((id) => {
                    this.state.selection.delete(id);
                    this.state.assigned.delete(id);
                });
                this.emit('selection:change', this.getState());
            }
            this.emit('items:removed', { collectionIds: ids });
        } catch (error) {
            this.#rollbackOptimistic(previousStates);
            const retryAction = () => this.#removeFromCollections(collectionIds, skipSelectionSync);
            this.lastFailedAction = retryAction;
            const detail = this.#emitError('items:error', error, {
                context: 'remove',
                fallback: 'Không thể xóa dự án khỏi danh sách.',
                retryable: Boolean(error?.retryable),
                retry: retryAction,
            });
            throw new Error(detail.error);
        }
    }

    #applyOperationResults(payload, ids, hasFdi) {
        if (Array.isArray(payload?.results)) {
            payload.results.forEach((result) => {
                if (!result) return;
                const id = Number(result.collection_id);
                if (!id) return;
                const success = Boolean(result.success);
                const collection = this.#findCollection(id);
                if (collection) {
                    collection.has_fdi = success ? hasFdi : !hasFdi && collection.has_fdi;
                }
            });
        } else {
            this.#applyOptimistic(ids, hasFdi);
        }

        const snapshot = this.getState();
        this.emit('collections:updated', snapshot);
        this.emit('state:change', snapshot);
    }

    #captureOptimisticStates(ids) {
        const snapshots = [];
        ids.forEach((id) => {
            const collection = this.#findCollection(id);
            if (collection) {
                snapshots.push({ id, has_fdi: collection.has_fdi });
            }
        });
        return snapshots;
    }

    #emitError(event, rawError, { context, fallback, retryable, retry } = {}) {
        const message = normaliseCollectionError(rawError, fallback);
        const detail = {
            error: message,
            context,
            retryable: Boolean(retryable),
            retry: typeof retry === 'function' ? retry : null,
            original: rawError,
        };
        this.emit(event, detail);
        return detail;
    }

    #applyOptimistic(ids, hasFdi) {
        ids.forEach((id) => {
            const collection = this.#findCollection(id);
            if (collection) {
                collection.has_fdi = hasFdi;
            }
        });
        this.emit('collections:updated', this.getState());
    }

    #rollbackOptimistic(snapshots) {
        snapshots.forEach(({ id, has_fdi }) => {
            const collection = this.#findCollection(id);
            if (collection) {
                collection.has_fdi = has_fdi;
            }
        });
        this.emit('collections:updated', this.getState());
    }

    #findCollection(id) {
        const numId = Number(id);
        return this.state.collections.find((collection) => Number(collection.id) === numId) || null;
    }

    #setLoading(flag) {
        this.state.loading = flag;
    }

    /**
     * Apply sync results to collections state.
     */
    #applySyncResults(addedResults, removedResults) {
        /* Process add results */
        if (Array.isArray(addedResults)) {
            addedResults.forEach((result) => {
                if (!result) return;
                const id = Number(result.collection_id);
                if (!id) return;
                const success = Boolean(result.success);
                const collection = this.#findCollection(id);
                if (collection) {
                    collection.has_fdi = success ? true : collection.has_fdi;
                }
            });
        }

        /* Process remove results */
        if (Array.isArray(removedResults)) {
            removedResults.forEach((result) => {
                if (!result) return;
                const id = Number(result.collection_id);
                if (!id) return;
                const success = Boolean(result.success);
                const collection = this.#findCollection(id);
                if (collection) {
                    collection.has_fdi = success ? false : collection.has_fdi;
                }
            });
        }

        /* Emit collection updates */
        this.emit('collections:updated', this.getState());
    }
}

export { CollectionManager };
export default CollectionManager;
